import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mic, Zap, Globe2, Shield, Play, Pause, ChevronRight, Check, Volume2, Wand2, BarChart2, Star, Loader2, Copy, FileAudio, Sparkles, PenTool, ChevronDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import WaveformBars from '../components/WaveformBars';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';
import ScrollToTop from '../components/ScrollToTop';

const TOOLS = [
  { icon: <Wand2 className="w-6 h-6" />, title: "Text to Speech", desc: "Convert any text to natural speech", color: "from-blue-500 to-cyan-500" },
  { icon: <Copy className="w-6 h-6" />, title: "Voice Cloning", desc: "Clone voices with 10s of audio", color: "from-purple-500 to-pink-500" },
  { icon: <FileAudio className="w-6 h-6" />, title: "Transcription", desc: "Speech to text in real-time", color: "from-cyan-500 to-blue-600" },
  { icon: <Sparkles className="w-6 h-6" />, title: "Voice Styles", desc: "Narrative, conversational & more", color: "from-amber-500 to-orange-500" },
  { icon: <PenTool className="w-6 h-6" />, title: "Script Creator", desc: "AI-powered script writing", color: "from-green-500 to-emerald-500" },
  { icon: <Globe2 className="w-6 h-6" />, title: "26+ Languages", desc: "Multilingual voice synthesis", color: "from-rose-500 to-red-500" },
];

const FEATURES = [
  { icon: <Mic className="w-6 h-6" />, title: "Voice Cloning", desc: "Clone any voice with just 10 seconds of audio. Perfect for personalized content." },
  { icon: <Globe2 className="w-6 h-6" />, title: "204+ AI Voices", desc: "Choose from 200+ voices across 26 languages including English, Chinese, Spanish, French & more." },
  { icon: <Zap className="w-6 h-6" />, title: "Instant Generation", desc: "Generate broadcast-ready speech in seconds using state-of-the-art neural voice synthesis." },
  { icon: <Shield className="w-6 h-6" />, title: "Studio Quality", desc: "22kHz MP3 output. Crystal clear, broadcast-ready audio every time." },
  { icon: <BarChart2 className="w-6 h-6" />, title: "Usage Analytics", desc: "Track your generation history, character usage and voice performance." },
  { icon: <Volume2 className="w-6 h-6" />, title: "Voice Controls", desc: "Adjust speed, pitch and volume in real-time. Fine-tune every generation." },
];

const REVIEWS = [
  { name: "Sarah Mitchell", role: "Content Creator", text: "VoiceraLab has completely transformed how I create content. The voice quality is indistinguishable from human narration.", color: "bg-rose-500" },
  { name: "James Chen", role: "Product Manager", text: "We switched from ElevenLabs and couldn't be happier. More voices, better quality, and the multilingual support is amazing.", color: "bg-blue-500" },
  { name: "Emily Watson", role: "Podcast Host", text: "The long-form audio feature is a game changer. I can generate full episodes without any breaks or quality loss.", color: "bg-purple-500" },
  { name: "Michael Johnson", role: "YouTuber", text: "Voice cloning accuracy is impressive. My clone sounds exactly like me. My audience can't tell the difference.", color: "bg-green-500" },
  { name: "Priya Sharma", role: "E-learning Developer", text: "We create courses in 12 languages using VoiceraLab. The consistency across languages is remarkable.", color: "bg-amber-500" },
  { name: "David Lee", role: "App Developer", text: "The API is clean and well-documented. Integration into our app took less than a day. Highly recommended.", color: "bg-cyan-500" },
  { name: "Amanda Torres", role: "Marketing Director", text: "Our video ads perform 3x better with VoiceraLab voices. The emotional range in different styles is impressive.", color: "bg-pink-500" },
  { name: "Robert Kim", role: "Audiobook Author", text: "I've published 6 audiobooks using VoiceraLab. The narrative voice style makes my stories come alive.", color: "bg-indigo-500" },
  { name: "Lisa Brown", role: "Teacher", text: "I use VoiceraLab daily for creating educational content. Students love the engaging voice styles.", color: "bg-teal-500" },
  { name: "Alex Rivera", role: "Film Maker", text: "The whisper and calm styles are perfect for documentaries. Studio-quality output every single time.", color: "bg-orange-500" },
  { name: "Nina Patel", role: "UX Designer", text: "We prototype voice interfaces using VoiceraLab. The variety of voices helps us test different personas.", color: "bg-violet-500" },
  { name: "Chris Taylor", role: "Entrepreneur", text: "Started using VoiceraLab for my startup's IVR system. Customer satisfaction improved significantly.", color: "bg-red-500" },
];

const FAQS = [
  { q: "What is VoiceraLab and how does it work?", a: "VoiceraLab is an AI-powered text-to-speech platform that converts written text into natural-sounding speech using advanced neural voice synthesis. Simply type or paste your text, choose a voice and style, and generate studio-quality audio in seconds." },
  { q: "How many voices and languages does VoiceraLab support?", a: "VoiceraLab offers 204+ AI voices across 26+ languages including English, Chinese, Spanish, French, German, Japanese, Korean, Arabic, Hindi, Bengali, and many more. Each voice comes with multiple accent options." },
  { q: "Can I clone my own voice?", a: "Yes! VoiceraLab supports voice cloning. Upload just 10-20 seconds of clean audio, and our AI will create a digital replica of the voice. You can then use this cloned voice for all your text-to-speech needs." },
  { q: "Is there a limit on how long the generated audio can be?", a: "No. VoiceraLab supports long-form audio generation. Whether you need a 30-second clip or a full one-hour audiobook chapter, our system automatically processes and merges the audio into a single continuous file." },
  { q: "What voice styles are available?", a: "We offer 9 distinct voice styles: Default, Conversational, Narrative, Newscast, Calm, Cheerful, Sad, Whisper, and Multilingual. Each style adjusts the tone and delivery of the voice to match your content needs." },
  { q: "What audio format is the output?", a: "All generated audio is delivered in high-quality MP3 format at 22kHz sample rate with 192kbps bitrate — broadcast-ready quality suitable for podcasts, videos, audiobooks, and professional productions." },
  { q: "How does the credit system work?", a: "New users receive 10,000 free characters. Credits are consumed based on the number of characters in your text. Our Creator plan offers 200,000 characters/month and the Pro plan offers 1,000,000 characters/month." },
  { q: "Can I use VoiceraLab for commercial projects?", a: "Absolutely! All audio generated through VoiceraLab can be used for commercial purposes including YouTube videos, podcasts, advertisements, e-learning courses, apps, and any other commercial projects." },
  { q: "Does VoiceraLab offer Speech-to-Text transcription?", a: "Yes! VoiceraLab includes a built-in speech-to-text feature. You can transcribe audio in real-time using your microphone or upload audio files for batch transcription in 15+ languages." },
  { q: "How is VoiceraLab different from ElevenLabs?", a: "VoiceraLab offers more voices (204+ vs ~30), more languages (26+ vs ~10), voice styling options, long-form audio without splits, and competitive pricing. Our neural synthesis engine delivers comparable quality at a fraction of the cost." },
  { q: "Is my data secure?", a: "Absolutely. We use industry-standard encryption for all data transfers. Your text inputs and generated audio are processed securely and are never shared with third parties. We comply with GDPR and CCPA regulations." },
  { q: "What payment methods do you accept?", a: "We accept all major credit and debit cards through our secure Stripe payment gateway, including Visa, Mastercard, American Express, and Discover. All transactions are protected with bank-level encryption." },
  { q: "Can I download the generated audio?", a: "Yes! Every generated audio file can be downloaded as an MP3 file with a single click. There are no restrictions on downloads — your audio, your content." },
  { q: "Do you offer an API for developers?", a: "Yes, VoiceraLab provides a RESTful API that developers can integrate into their applications. Our API supports all features including TTS generation, voice cloning, and transcription." },
  { q: "What happens when I run out of credits?", a: "When your free credits are exhausted, you can upgrade to our Creator ($9/month) or Pro ($29/month) plans for more characters. Your account and generated audio remain accessible even after credits run out." },
];

const PRICING = [
  { name: "Free", price: "$0", period: "/forever", credits: "10,000", features: ["204+ AI Voices", "26+ Languages", "All Voice Styles", "Audio Download", "Speech to Text"], highlight: false, planId: "free" },
  { name: "Creator", price: "$9", period: "/month", credits: "200,000", features: ["Everything in Free", "Voice Cloning", "Priority Support", "Long-form Audio", "Commercial License"], highlight: true, planId: "creator" },
  { name: "Pro", price: "$29", period: "/month", credits: "1,000,000", features: ["Everything in Creator", "Custom Voices", "Dedicated Support", "Bulk Processing", "Advanced Analytics"], highlight: false, planId: "pro" },
];

const ReviewCard = ({ name, role, text, color }) => (
  <div className="flex-shrink-0 w-72 sm:w-80 glass-card rounded-2xl p-5 border border-border">
    <div className="flex items-center gap-3 mb-3">
      <div className={`w-10 h-10 rounded-full ${color} flex items-center justify-center text-white font-bold text-sm`}>
        {name[0]}
      </div>
      <div>
        <p className="font-medium text-sm text-foreground">{name}</p>
        <p className="text-xs text-muted-foreground">{role}</p>
      </div>
    </div>
    <div className="flex gap-0.5 mb-2">
      {[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />)}
    </div>
    <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">{text}</p>
  </div>
);

const FAQItem = ({ q, a, open, onClick }) => (
  <div className="glass-card rounded-xl border border-border overflow-hidden transition-all">
    <button onClick={onClick} className="w-full text-left px-5 py-4 flex items-center justify-between gap-3">
      <span className="text-sm font-medium text-foreground">{q}</span>
      <ChevronDown className={`w-4 h-4 text-muted-foreground flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
    </button>
    {open && <div className="px-5 pb-4 text-sm text-muted-foreground leading-relaxed border-t border-border pt-3">{a}</div>}
  </div>
);

export default function LandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const audioRef = useRef(null);
  const [demoPlaying, setDemoPlaying] = useState(false);
  const [demoUrl, setDemoUrl] = useState('');
  const [demoLoading, setDemoLoading] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => { document.title = 'VoiceraLab | The Future of AI Voice Creation'; }, []);
  const goToDashboard = () => navigate(user ? '/dashboard' : '/auth?mode=register');

  const handleDemoPlay = async () => {
    if (demoPlaying) { audioRef.current?.pause(); setDemoPlaying(false); return; }
    if (demoUrl) { audioRef.current.src = demoUrl; audioRef.current.play(); setDemoPlaying(true); return; }
    setDemoLoading(true);
    try {
      const r = await fetch(`${API}/api/demo/audio`);
      const d = await r.json();
      if (d.url) {
        setDemoUrl(d.url);
        audioRef.current.src = d.url;
        audioRef.current.play();
        setDemoPlaying(true);
      }
    } catch { /* ignore */ }
    setDemoLoading(false);
  };

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onEnd = () => setDemoPlaying(false);
    a.addEventListener('ended', onEnd);
    return () => a.removeEventListener('ended', onEnd);
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      <SiteHeader />

      {/* Hero with Video Background */}
      <section className="relative min-h-screen flex items-center justify-center pt-16 overflow-hidden">
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <video autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover opacity-20 dark:opacity-10">
            <source src="https://cdn.coverr.co/videos/coverr-sound-wave-visualization-1920/1080p.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/60 to-background" />
          <div className="blob-animate absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-blue-500/15 dark:bg-blue-500/10 rounded-full blur-[100px]" />
          <div className="blob-animate-delay absolute top-1/3 right-1/4 w-[500px] h-[500px] bg-purple-500/12 dark:bg-purple-500/8 rounded-full blur-[100px]" />
        </div>
        <div className="max-w-6xl mx-auto px-6 text-center space-y-8">
          <div className="anim-fadeup inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm">
            <Wand2 className="w-4 h-4" /> 204+ Voices · 26 Languages · Neural AI Voice Synthesis
          </div>
          <h1 className="anim-fadeup-d1 text-5xl md:text-7xl font-bold font-outfit leading-tight">
            <span className="text-gradient">Transform Text</span><br/><span className="text-foreground">Into Natural Voice</span>
          </h1>
          <p className="anim-fadeup-d2 text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Generate lifelike speech in seconds with 204+ AI voices across 26 languages. Perfect for content creators, businesses, and developers.
          </p>
          <div className="anim-fadeup-d3 flex flex-col sm:flex-row gap-4 justify-center">
            <button data-testid="hero-get-started-btn" onClick={() => navigate('/auth?mode=register')} className="gradient-btn px-8 py-4 rounded-full text-lg font-semibold flex items-center justify-center gap-2">
              Start for Free <ChevronRight className="w-5 h-5" />
            </button>
            <button onClick={() => document.getElementById('voices')?.scrollIntoView({ behavior: 'smooth' })} className="px-8 py-4 rounded-full text-lg font-medium border border-border text-foreground hover:bg-secondary transition-colors flex items-center justify-center gap-2">
              <Play className="w-5 h-5" /> Listen to Voices
            </button>
          </div>
          {/* Demo Player */}
          <div className="anim-fadeup-d4 glass-card rounded-3xl p-8 max-w-2xl mx-auto">
            <audio ref={audioRef} preload="none" />
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                  <Volume2 className="w-5 h-5 text-white" />
                </div>
                <div className="text-left">
                  <p className="font-medium text-sm text-foreground">Cherry — American English</p>
                  <p className="text-xs text-muted-foreground">VoiceraLab AI · Live Demo</p>
                </div>
              </div>
              <button data-testid="demo-play-btn" onClick={handleDemoPlay} disabled={demoLoading} className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center hover:opacity-80 transition-opacity disabled:opacity-60 shadow-lg shadow-blue-500/30">
                {demoLoading ? <Loader2 className="w-5 h-5 text-white animate-spin" /> : demoPlaying ? <Pause className="w-5 h-5 text-white" /> : <Play className="w-5 h-5 text-white ml-0.5" />}
              </button>
            </div>
            <WaveformBars isPlaying={demoPlaying} count={40} height={56} />
            <p className="mt-4 text-sm text-muted-foreground italic text-left">"Hi there. Welcome to VoiceraLab — where your words become natural, expressive speech in just seconds."</p>
          </div>
        </div>
      </section>

      {/* Tools Slider */}
      <section className="py-12 border-b border-border overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-center text-sm text-muted-foreground uppercase tracking-widest mb-6">Our Tools</p>
          <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide">
            {TOOLS.map((tool, i) => (
              <button key={i} onClick={goToDashboard} className="snap-start flex-shrink-0 w-48 glass-card card-hover rounded-2xl p-5 border border-border text-left transition-all group">
                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${tool.color} flex items-center justify-center text-white mb-3 group-hover:scale-110 transition-transform`}>{tool.icon}</div>
                <p className="font-semibold text-sm text-foreground font-outfit">{tool.title}</p>
                <p className="text-xs text-muted-foreground mt-1">{tool.desc}</p>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 border-b border-border">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-primary text-sm uppercase tracking-widest font-medium">Everything you need</p>
            <h2 className="text-3xl sm:text-4xl font-bold font-outfit text-foreground mt-3">Powerful Features</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <div key={i} className="glass-card card-hover rounded-2xl p-6 border border-border transition-all group">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary mb-4 group-hover:scale-110 transition-transform">{f.icon}</div>
                <h3 className="font-semibold font-outfit text-foreground">{f.title}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Voice Showcase with Unique Avatars */}
      <section id="voices" className="py-20 border-b border-border">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-primary text-sm uppercase tracking-widest font-medium">Voice Library</p>
          <h2 className="text-3xl sm:text-4xl font-bold font-outfit text-foreground mt-3 mb-4">204+ Premium AI Voices</h2>
          <p className="text-muted-foreground max-w-xl mx-auto mb-8">26 languages. Multiple accents. Male and female voices. From conversational to professional narration.</p>
          {/* Featured voices with unique avatars */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-3xl mx-auto mb-8">
            {[
              { name: "Cherry", gender: "female", lang: "English", color: "from-rose-400 to-pink-600" },
              { name: "Ethan", gender: "male", lang: "English", color: "from-blue-400 to-indigo-600" },
              { name: "Serena", gender: "female", lang: "French", color: "from-purple-400 to-fuchsia-600" },
              { name: "Ryan", gender: "male", lang: "German", color: "from-cyan-400 to-blue-600" },
              { name: "Jada", gender: "female", lang: "Spanish", color: "from-amber-400 to-orange-500" },
              { name: "Marcus", gender: "male", lang: "Japanese", color: "from-emerald-400 to-green-600" },
              { name: "Jennifer", gender: "female", lang: "Korean", color: "from-violet-400 to-purple-600" },
              { name: "Dylan", gender: "male", lang: "Chinese", color: "from-teal-400 to-cyan-600" },
            ].map((v, i) => (
              <div key={i} className="glass-card rounded-xl p-4 border border-border text-center group hover:border-primary/30 transition-all">
                <div className={`w-12 h-12 mx-auto rounded-full bg-gradient-to-br ${v.color} flex items-center justify-center mb-2 group-hover:scale-110 transition-transform shadow-lg`}>
                  {v.gender === 'female' ? (
                    <svg viewBox="0 0 24 24" className="w-6 h-6 text-white/90" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="8" r="4" /><path d="M12 12c-4 0-7 2-7 5v1h14v-1c0-3-3-5-7-5z" /><path d="M8 6.5c0-2 1-4 4-4s4 2 4 4" strokeLinecap="round" /></svg>
                  ) : (
                    <svg viewBox="0 0 24 24" className="w-6 h-6 text-white/90" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="8" r="4" /><path d="M12 12c-4 0-7 2-7 5v1h14v-1c0-3-3-5-7-5z" /><path d="M7 7c0-3 2-5 5-5s5 2 5 5" strokeLinecap="round" /></svg>
                  )}
                </div>
                <p className="font-medium text-sm text-foreground">{v.name}</p>
                <p className="text-xs text-muted-foreground">{v.gender === 'female' ? 'Female' : 'Male'} · {v.lang}</p>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap justify-center gap-3 max-w-3xl mx-auto">
            {["English","Chinese","Spanish","French","German","Japanese","Korean","Italian","Portuguese","Russian","Arabic","Hindi","Bengali","Dutch","Turkish","Swedish","Polish","Vietnamese","Thai","Greek","Czech","Romanian","Malay","Indonesian","Norwegian"].map(l => (
              <span key={l} className="px-4 py-2 rounded-full bg-secondary/80 text-sm text-foreground border border-border">{l}</span>
            ))}
          </div>
          <button onClick={goToDashboard} className="mt-8 gradient-btn px-8 py-3 rounded-full text-sm font-semibold inline-flex items-center gap-2">
            Explore All Voices <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* Reviews - 2 Row Auto-scrolling Slider */}
      <section className="py-20 border-b border-border overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 text-center mb-10">
          <p className="text-primary text-sm uppercase tracking-widest font-medium">Trusted by thousands</p>
          <h2 className="text-3xl sm:text-4xl font-bold font-outfit text-foreground mt-3">What Our Users Say</h2>
        </div>
        <div className="space-y-4">
          {/* Row 1 - scrolls left */}
          <div className="reviews-scroll-left">
            <div className="flex gap-4 animate-scroll-left">
              {[...REVIEWS.slice(0, 6), ...REVIEWS.slice(0, 6)].map((r, i) => (
                <ReviewCard key={i} {...r} />
              ))}
            </div>
          </div>
          {/* Row 2 - scrolls right */}
          <div className="reviews-scroll-right">
            <div className="flex gap-4 animate-scroll-right">
              {[...REVIEWS.slice(6), ...REVIEWS.slice(6)].map((r, i) => (
                <ReviewCard key={i} {...r} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 border-b border-border">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-primary text-sm uppercase tracking-widest font-medium">Simple pricing</p>
            <h2 className="text-3xl sm:text-4xl font-bold font-outfit text-foreground mt-3">Choose Your Plan</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {PRICING.map((plan, i) => (
              <div key={i} className={`glass-card rounded-2xl p-6 border transition-all ${plan.highlight ? 'border-primary ring-2 ring-primary/20 scale-105' : 'border-border'}`}>
                {plan.highlight && (
                  <span className="inline-block px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-semibold mb-3">Most Popular</span>
                )}
                <h3 className="font-bold font-outfit text-foreground text-xl">{plan.name}</h3>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-foreground">{plan.price}</span>
                  <span className="text-muted-foreground text-sm">{plan.period}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-2">{plan.credits} characters/month</p>
                <ul className="mt-5 space-y-2.5">
                  {plan.features.map((f, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-foreground">
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />{f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={goToDashboard}
                  className={`mt-6 w-full py-3 rounded-xl font-semibold text-sm transition-all ${plan.highlight ? 'gradient-btn' : 'bg-secondary text-foreground hover:bg-secondary/70'}`}
                >
                  {plan.price === "$0" ? 'Start Free' : 'Get Started'}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 border-b border-border">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-primary text-sm uppercase tracking-widest font-medium">FAQ</p>
            <h2 className="text-3xl sm:text-4xl font-bold font-outfit text-foreground mt-3">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-3">
            {FAQS.map((faq, i) => (
              <FAQItem key={i} q={faq.q} a={faq.a} open={openFaq === i} onClick={() => setOpenFaq(openFaq === i ? -1 : i)} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 border-b border-border">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold font-outfit text-foreground">Ready to Transform Your Content?</h2>
          <p className="text-muted-foreground mt-4 text-lg">Join thousands of creators using VoiceraLab. Start free — no credit card required.</p>
          <button onClick={goToDashboard} className="mt-8 gradient-btn px-10 py-4 rounded-full text-lg font-semibold inline-flex items-center gap-2">
            Start Creating Now <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      <SiteFooter />
      <ScrollToTop />
    </div>
  );
}
